import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search, Send, Paperclip, X, ArrowLeft, Info, FileText, Download, Play, User, Phone,
  ChevronDown, RefreshCw, Check, CheckCheck, Plus, UserCheck, CheckCircle2, StickyNote,
  Link2, Receipt, Ticket, Zap, History, Users, ArrowDown, ArrowUp, ChevronRight, Image as ImageIcon, SlidersHorizontal,
} from 'lucide-react';
import { whatsappInboxAPI, openInboxStream, paymentAPI, complaintAPI, whatsappAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useDarkReady from '../theme/useDarkReady';
import './whatsappInbox.css';

// Backstop only — the SSE stream (openInboxStream) is what normally keeps this page current.
// These stay slow so a dropped stream degrades to "a bit behind" rather than "broken".
const LIST_POLL_MS = 20000;
const CHAT_POLL_MS = 20000;
const LIVE_POLL_MS = 1000; // matches the NAS poller's own 1s cadence; the backend caches everything external
const WIDE_PX = 1200;
const NARROW_PX = 860;

const fmtTime = (d) => new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
export const isSameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
export const fmtDay = (d) => {
  const now = new Date();
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, new Date(now.getTime() - 86400000))) return 'Yesterday';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtListTime = (d) => (!d ? '' : isSameDay(d, new Date()) ? fmtTime(d) : fmtDay(d) === 'Yesterday' ? 'Yesterday'
  : new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Same precedence as web.whatsapp.com: the name saved in the phone's address book first, then the
// CRM customer, then the name the contact chose for themselves, then the bare number.
const displayName = (chat) => (chat.isGroup
  ? (chat.name || 'Unnamed group')
  : chat.savedName || chat.customers?.[0]?.name || chat.name || (chat.phone ? `+${chat.phone}` : 'Unknown'));

// Who wrote a group message: the phone's saved name, else the CRM customer, else their own
// WhatsApp profile name, else the bare number.
const senderLabel = (m) => m.senderSavedName || m.senderCustomerName || m.senderName || (m.senderPhone ? `+${m.senderPhone}` : 'Someone');
// A stable colour per sender, so a long group thread is easy to follow.
const SENDER_COLOURS = ['var(--c-c2410c)', 'var(--c-0f766e)', 'var(--c-7c3aed)', 'var(--c-be123c)', 'var(--c-1d4ed8)', 'var(--c-4d7c0f)', 'var(--c-a16207)', 'var(--c-0e7490)'];
const senderColour = (label) => SENDER_COLOURS[[...String(label)].reduce((a, c) => a + c.charCodeAt(0), 0) % SENDER_COLOURS.length];
const initials = (s) => (String(s).replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('') || '#').toUpperCase();

// 919876543210 -> +91 98765 43210 (other lengths are shown as-is).
const fmtPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '');
  if (!d) return '';
  return d.length === 12 && d.startsWith('91') ? `+91 ${d.slice(2, 7)} ${d.slice(7)}` : `+${d}`;
};
const MEDIA_PREVIEW = { image: '📷 Photo', video: '🎥 Video', audio: '🎤 Voice message', document: '📄 Document', sticker: 'Sticker' };
const previewText = (chat) => chat.lastMessageText || MEDIA_PREVIEW[chat.lastMessageType]
  || (chat.isGroup ? `Group · ${chat.participantCount || 0} members` : '');
// Touch keyboards have no Shift, so there Enter must insert a new line and the Send button sends.
const isTouchDevice = () => !!window.matchMedia?.('(pointer: coarse)').matches;

const COMPLAINT_CATEGORIES = [
  { value: 'no_internet', label: 'No Internet' },
  { value: 'slow_internet', label: 'Slow Internet' },
  { value: 'red_light_router', label: 'Red Light on Router' },
  { value: 'cable_cut', label: 'Cable Cut' },
  { value: 'router_issue', label: 'Router Issue' },
  { value: 'e61_no_signal', label: 'E61 / No Signal' },
  { value: 'e51_channel_error', label: 'E51 / Channel Error' },
  { value: 'channel_stuck', label: 'Channel Stuck' },
  { value: 'colors_only', label: 'Colors Only' },
  { value: 'relocation_transfer', label: 'Relocation / Transfer' },
  { value: 'billing_dispute', label: 'Billing Dispute' },
  { value: 'plan_change', label: 'Plan Change' },
  { value: 'device_issue', label: 'Device Issue' },
  { value: 'other', label: 'Other' },
];

// Exported pieces (useWidth, Bubble, ChatRow, CustomerStrip, CustomerCard, fmtDay, isSameDay)
// are reused by the Kapso inbox (KapsoPage.js) so both inboxes look the same.
export function useWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

// The part of the screen that is actually visible. On iOS the on-screen keyboard does not shrink the
// layout viewport, so a plain `bottom: 0` composer ends up hidden behind it; the visual viewport does
// shrink, so the full-screen chat is sized and offset from it. `keyboard` is true while it is open.
function useVisualViewport() {
  const read = () => {
    const v = window.visualViewport;
    return { height: v ? v.height : window.innerHeight, top: v ? v.offsetTop : 0, keyboard: !!v && window.innerHeight - v.height > 150 };
  };
  const [box, setBox] = useState(read);
  useEffect(() => {
    const v = window.visualViewport;
    const on = () => setBox(read());
    on();
    window.addEventListener('resize', on);
    if (v) { v.addEventListener('resize', on); v.addEventListener('scroll', on); }
    return () => {
      window.removeEventListener('resize', on);
      if (v) { v.removeEventListener('resize', on); v.removeEventListener('scroll', on); }
    };
  }, []);
  return box;
}

// Object URLs for already-fetched media, so re-renders never refetch a file.
const mediaCache = new Map();
async function loadMedia(messageId) {
  if (mediaCache.has(messageId)) return mediaCache.get(messageId);
  const res = await whatsappInboxAPI.getMediaBlob(messageId);
  const url = URL.createObjectURL(res.data);
  mediaCache.set(messageId, url);
  return url;
}

function MediaBody({ msg }) {
  const auto = msg.type === 'image' || msg.type === 'sticker' || msg.type === 'audio';
  const [url, setUrl] = useState(mediaCache.get(msg.waId) || null);
  const [state, setState] = useState(mediaCache.has(msg.waId) ? 'ready' : 'idle');

  const load = useCallback(async () => {
    setState('loading');
    try { setUrl(await loadMedia(msg.waId)); setState('ready'); } catch { setState('error'); }
  }, [msg.waId]);

  useEffect(() => { if (auto && msg.hasMedia && state === 'idle') load(); }, [auto, msg.hasMedia, state, load]);

  if (!msg.hasMedia) return <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 13 }}>Media not available</div>;
  if (state === 'error') return <button className="btn btn-secondary" onClick={load} style={{ fontSize: 12 }}><RefreshCw size={12} /> Retry loading</button>;
  if (state === 'loading' || (auto && !url)) return <div style={{ width: 180, height: 100, background: 'var(--bg)', borderRadius: 6, display: 'grid', placeItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>;

  if (msg.type === 'image' || msg.type === 'sticker') {
    return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" style={{ maxWidth: 'min(260px, 100%)', maxHeight: 300, borderRadius: 6, display: 'block' }} /></a>;
  }
  if (msg.type === 'audio') return <audio controls src={url} style={{ width: 'min(240px, 100%)' }} />;
  if (msg.type === 'video') {
    if (!url) return <button className="btn btn-secondary" onClick={load}><Play size={14} /> Play video</button>;
    return <video controls src={url} style={{ maxWidth: 'min(280px, 100%)', maxHeight: 300, borderRadius: 6 }} />;
  }
  if (!url) return <button className="btn btn-secondary" onClick={load} style={{ fontSize: 13 }}><FileText size={14} /> {msg.fileName || 'Document'}</button>;
  return <a className="btn btn-secondary" href={url} download={msg.fileName || 'file'} style={{ fontSize: 13, textDecoration: 'none' }}><Download size={14} /> {msg.fileName || 'Download'}</a>;
}

// WhatsApp's own tick marks, driven by the delivery acks the gateway records.
function Ticks({ status }) {
  if (status === 'read') return <CheckCheck size={14} color="var(--c-53bdeb)" style={{ verticalAlign: -3 }} />;
  if (status === 'delivered') return <CheckCheck size={14} style={{ verticalAlign: -3 }} />;
  return <Check size={14} style={{ verticalAlign: -3 }} />;
}

export const Bubble = React.memo(function Bubble({ msg, showSender }) {
  const out = msg.direction === 'out';
  const who = showSender ? senderLabel(msg) : '';
  return (
    <div style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', margin: '2px 0' }}>
      <div style={{
        maxWidth: 'min(82%, 520px)', padding: '6px 10px', borderRadius: 8, fontSize: 14.5, lineHeight: 1.4,
        background: out ? 'var(--cb-d9fdd3)' : 'var(--cb-ffffff)', boxShadow: '0 1px 1px rgba(0,0,0,.13)', wordBreak: 'break-word',
        opacity: msg.pending ? 0.6 : 1,
      }}>
        {who && <div style={{ fontSize: 12, fontWeight: 600, color: senderColour(who), marginBottom: 2 }}>{who}</div>}
        {msg.type !== 'text' && msg.type !== 'other' && !msg.pending && <div style={{ marginBottom: msg.text ? 4 : 0 }}><MediaBody msg={msg} /></div>}
        {msg.text && <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2, display: 'flex', gap: 3, justifyContent: 'flex-end', alignItems: 'center' }}>
          {msg.pending ? 'sending…' : fmtTime(msg.timestamp)}
          {out && !msg.pending && <Ticks status={msg.status} />}
        </div>
      </div>
    </div>
  );
});

export function Avatar({ name, isGroup, size = 46 }) {
  return (
    <div className="wi-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: isGroup ? 'var(--cb-bfdbfe)' : 'var(--cb-cbd5e1)' }}>
      {isGroup ? <Users size={Math.round(size * 0.42)} /> : initials(name)}
    </div>
  );
}

// One compact row: who, last message, when, unread count. A third muted line carries the number and
// anything that needs attention (not linked, awaiting a reply, who has it).
export function ChatRow({ chat, active, onClick }) {
  const name = displayName(chat);
  const unread = chat.unreadCount > 0;
  const needsReply = !unread && !chat.isGroup && chat.lastMessageDirection === 'in' && chat.chatStatus !== 'resolved';
  const number = !chat.isGroup && chat.phone && !name.startsWith('+') ? fmtPhone(chat.phone) : '';
  const prefix = chat.lastMessageDirection === 'out' ? 'You: ' : (chat.isGroup && chat.lastMessageSender ? `${chat.lastMessageSender}: ` : '');
  const linked = chat.isGroup || chat.customers?.length;
  const hasMeta = number || needsReply || !linked || chat.assignedToName || chat.chatStatus === 'resolved';
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className={`wi-row${active ? ' on' : ''}${unread ? ' unread' : ''}`}>
      <Avatar name={name} isGroup={chat.isGroup} />
      <div className="wi-row-main">
        <div className="wi-row-line">
          <span className="wi-name">{name}</span>
          <span className="wi-time">{fmtListTime(chat.lastMessageAt)}</span>
        </div>
        <div className="wi-row-line">
          <span className="wi-prev">{prefix}{previewText(chat)}</span>
          {unread && <span className="wi-badge" aria-label={`${chat.unreadCount} unread`}>{chat.unreadCount > 99 ? '99+' : chat.unreadCount}</span>}
        </div>
        {hasMeta && (
          <div className="wi-row-meta">
            {number && <span>{number}</span>}
            {needsReply && <span className="wi-tag warn">Needs reply</span>}
            {!linked && <span className="wi-tag warn">Not linked</span>}
            {chat.assignedToName && <span className="wi-tag"><UserCheck size={10} /> {chat.assignedToName}</span>}
            {chat.chatStatus === 'resolved' && <span className="wi-tag ok">Resolved</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// Centred dialog on desktop. On a phone it becomes a full-width panel from the top (so a field that
// opens the keyboard is never hidden behind it); variant 'bottom' is a bottom sheet for tap-only
// menus, 'full' a full-screen page. Rendered into <body> so no page transform can displace it.
function Modal({ title, onClose, children, width = 460, variant = '' }) {
  return createPortal(
    <div onClick={onClose} className={`wi-modal-ov ${variant}`} data-no-ptr>
      <div onClick={(e) => e.stopPropagation()} className="wi-modal" style={{ maxWidth: width }}>
        <div className="wi-modal-head">
          <b style={{ flex: 1, minWidth: 0 }}>{title}</b>
          <button onClick={onClose} aria-label="Close" className="wi-icon-btn"><X size={20} /></button>
        </div>
        <div className="wi-modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function NewChatModal({ onClose, onOpened }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      whatsappInboxAPI.searchCustomers(q.trim())
        .then(r => setResults(r.data.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const open = async (payload, label) => {
    setBusy(true);
    try {
      const res = await whatsappInboxAPI.openChat(payload);
      onOpened(res.data.data, label);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not open the chat');
    } finally { setBusy(false); }
  };

  const digits = q.replace(/\D/g, '');
  return (
    <Modal title="New chat" onClose={onClose}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer by name, ID or number"
        style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14 }} />
      {digits.length === 10 && (
        <button className="btn btn-secondary" disabled={busy} onClick={() => open({ mobile: digits }, `+91${digits}`)} style={{ marginTop: 10, width: '100%' }}>
          Message +91 {digits} (not in CRM)
        </button>
      )}
      <div style={{ marginTop: 10 }}>
        {searching && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>}
        {!searching && q.trim().length >= 2 && results.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No customers found.</div>}
        {results.map(c => (
          <div key={c._id} onClick={() => !busy && open({ customerId: c._id }, c.name)} style={{ padding: '8px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {c.customerId || '—'} · +{c.mobile} · {c.customerType}{c.address?.area ? ` · ${c.address.area}` : ''}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function TemplateModal({ customer, onClose, onSend }) {
  const [templates, setTemplates] = useState(null);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    whatsappInboxAPI.getTemplates().then(r => setTemplates(r.data.data)).catch(() => setTemplates([]));
  }, []);

  const filtered = (templates || []).filter(t =>
    !q.trim() || `${t.name} ${t.category} ${t.body}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <Modal title={`Send a saved template to ${customer.name}`} onClose={onClose} width={560}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter templates"
        style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, marginBottom: 10 }} />
      {templates === null && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading templates…</div>}
      {templates && filtered.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No templates match.</div>}
      {filtered.map(t => (
        <div key={t._id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <b style={{ fontSize: 13 }}>{t.name}</b>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{String(t.category || '').replace(/_/g, ' ')}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', margin: '6px 0', maxHeight: 90, overflow: 'hidden' }}>{t.body}</div>
          <button className="btn btn-primary" disabled={busyId} style={{ fontSize: 12 }}
            onClick={async () => { setBusyId(t._id); await onSend(t); setBusyId(null); }}>
            {busyId === t._id ? 'Sending…' : 'Send'}
          </button>
        </div>
      ))}
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Placeholders like {'{{name}}'} and {'{{amount}}'} are filled in from this customer's record before sending.
      </div>
    </Modal>
  );
}

function PaymentLinkModal({ customer, onClose, onSend }) {
  const due = Math.round(customer.outstandingBalance || 0);
  const [amount, setAmount] = useState(due > 0 ? String(due) : '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Send payment link to ${customer.name}`} onClose={onClose}>
      <div style={{ fontSize: 13, marginBottom: 10 }}>
        Outstanding balance: <b>{money(due)}</b>
        {due <= 0 && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Nothing is due — enter an amount to collect in advance.</div>}
      </div>
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Amount (₹)</label>
      <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric"
        style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, marginBottom: 12 }} />
      <button className="btn btn-primary" disabled={busy || !amount} style={{ width: '100%' }}
        onClick={async () => { setBusy(true); await onSend(amount); setBusy(false); }}>
        {busy ? 'Sending…' : `Send pay link for ₹${amount || 0}`}
      </button>
    </Modal>
  );
}

function ComplaintModal({ customer, onClose, onCreated }) {
  const [category, setCategory] = useState('no_internet');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !description.trim()) return toast.error('Title and description are required');
    setBusy(true);
    try {
      const res = await complaintAPI.create({ customer: customer._id, category, title: title.trim(), description: description.trim(), priority });
      onCreated(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not raise the complaint');
    } finally { setBusy(false); }
  };

  const field = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, marginBottom: 10, fontFamily: 'inherit' };
  return (
    <Modal title={`Raise a complaint for ${customer.name}`} onClose={onClose}>
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Category</label>
      <select value={category} onChange={(e) => setCategory(e.target.value)} style={field}>
        {COMPLAINT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Priority</label>
      <select value={priority} onChange={(e) => setPriority(e.target.value)} style={field}>
        <option value="low">Low</option><option value="medium">Medium</option>
        <option value="high">High</option><option value="critical">Critical</option>
      </select>
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Title</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" style={field} />
      <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Description</label>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="What the customer reported" style={{ ...field, resize: 'vertical' }} />
      <button className="btn btn-primary" onClick={submit} disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Creating…' : 'Create ticket'}
      </button>
    </Modal>
  );
}

// Pulls in conversations that already existed before the inbox was built. WhatsApp only sends a
// device its chat history at the moment that device is linked, and never afterwards — so this
// pairs a second, temporary device to receive it, leaving the live sending session connected
// throughout. See whatsapp/src/whatsapp/historyImport.js.
function HistoryImportModal({ onClose, onImported }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const poll = () => whatsappAPI.getHistoryImport()
      .then(r => { if (!cancelled) setState(r.data.data); })
      .catch(() => {});
    poll();
    // 2s: a WhatsApp pairing QR rotates every 20-60s, so the panel has to keep up with it.
    const t = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const start = async () => {
    setBusy(true); setError('');
    try {
      const r = await whatsappAPI.startHistoryImport();
      setState(r.data.data);
    } catch (err) { setError(err.response?.data?.message || 'Could not start the import'); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await whatsappAPI.stopHistoryImport();
      toast.success('Import device unlinked');
      onImported();
      onClose();
    } catch (err) { setError(err.response?.data?.message || 'Could not unlink the import device'); }
    finally { setBusy(false); }
  };

  const status = state?.status || 'idle';
  const p = state?.progress || {};
  const box = { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginTop: 12 };

  return (
    <Modal title="Import old chats" onClose={onClose} width={520}>
      {status === 'idle' && (
        <>
          <p style={{ fontSize: 13, lineHeight: 1.5, marginTop: 0 }}>
            WhatsApp only sends past conversations to a device at the moment it is linked, so the
            running connection can't fetch them. This links a <b>second, temporary device</b> to
            receive that history.
          </p>
          <ol style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18 }}>
            <li>Press Start — a QR code appears here.</li>
            <li>On the phone: WhatsApp → <b>Linked devices</b> → <b>Link a device</b> → scan it.</li>
            <li>Keep the phone unlocked, online and on WhatsApp for ~10 minutes.</li>
            <li>When the counters stop rising, press <b>Finish &amp; unlink</b>.</li>
          </ol>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            WhatsApp decides how much it sends — usually the recent months, mostly text. Old photos
            and videos generally don't come through.
          </p>
          <p style={{ fontSize: 12, color: 'var(--c-92400e)', background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 6, padding: '6px 8px' }}>
            <b>Heads up:</b> in practice, linking a second device has made WhatsApp log the live
            connection out. If that happens, reconnect it from the WhatsApp API page and scan its
            QR again. Messages sent in the meantime are queued, not lost. Run this at a quiet time.
          </p>
          <button className="btn btn-primary" onClick={start} disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Starting…' : 'Start import'}
          </button>
        </>
      )}

      {status === 'connecting' && <div style={{ fontSize: 13 }}>Preparing the pairing code…</div>}

      {status === 'qr' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            Phone → WhatsApp → <b>Linked devices</b> → <b>Link a device</b>, then scan this:
          </div>
          {state.qr
            ? <img src={state.qr} alt="Pairing QR code" style={{ width: 260, height: 260 }} />
            : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Generating code…</div>}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            The code refreshes by itself until it is scanned.
          </div>
        </div>
      )}

      {status === 'syncing' && (
        <>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-0ca30c)' }}>Linked — receiving history</div>
          <div style={box}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span>Messages imported</span><b>{p.messages || 0}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span>Conversations seen</span><b>{p.chats || 0}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span>Batches received</span><b>{p.chunks || 0}</b>
            </div>
            {p.lastAt && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                Last batch {fmtTime(p.lastAt)}{p.progress != null ? ` · WhatsApp reports ${p.progress}%` : ''}
              </div>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Counters can pause between batches. Give it a few minutes with the phone unlocked; unlink
            once they have clearly stopped.
          </p>
          <button className="btn btn-primary" onClick={finish} disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Unlinking…' : 'Finish & unlink'}
          </button>
        </>
      )}

      {status === 'error' && (
        <>
          <div style={{ fontSize: 13, color: 'var(--danger, #dc2626)' }}>{state.error || 'The import failed.'}</div>
          {(p.messages > 0) && <div style={{ fontSize: 13, marginTop: 8 }}>{p.messages} messages arrived before it stopped, and have been kept.</div>}
          <button className="btn btn-primary" onClick={start} disabled={busy} style={{ width: '100%', marginTop: 12 }}>Try again</button>
          <button className="btn btn-secondary" onClick={finish} disabled={busy} style={{ width: '100%', marginTop: 8 }}>Unlink and close</button>
        </>
      )}

      {error && <div style={{ fontSize: 13, color: 'var(--danger, #dc2626)', marginTop: 10 }}>{error}</div>}
    </Modal>
  );
}

const TYPE_LABEL = { catv: 'CableTV', broadband: 'Broadband', both: 'CableTV + Broadband' };
const STATUS_COLOUR = { active: 'var(--cb-0ca30c)', inactive: 'var(--cb-6b7280)', suspended: 'var(--cb-dc2626)' };

// "in 5 days" / "expired 3 days ago" — how a support person actually reads an expiry date.
function expiryNote(d) {
  const days = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `expired ${Math.abs(days)}d ago`, bad: true };
  if (days === 0) return { text: 'expires today', bad: true };
  return { text: days <= 5 ? `in ${days}d` : fmtDate(d), bad: days <= 5 };
}

function fmtBytes(b) {
  if (!b || b <= 0) return '0 MB';
  const mb = Number(b) / 1048576;
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
}
function fmtMbps(v) {
  const n = Number(v) || 0;
  return `${n.toFixed(n >= 10 ? 0 : 2)} Mbps`;
}
function fmtUptime(since) {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const d = Math.floor(secs / 86400), h = Math.floor((secs % 86400) / 3600), m = Math.floor((secs % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Connection state, speed, session and today's usage for a broadband customer. Only ever rendered
// with data the backend actually returned — an absent value is left out, never shown as "0".
function LiveChips({ live, history }) {
  if (!live) return null;
  const online = live.online;
  const blocked = live.status === 'Blocked';
  const dot = blocked ? 'var(--cb-dc2626)' : online ? 'var(--cb-0ca30c)' : online === false ? 'var(--cb-dc2626)' : 'var(--cb-9ca3af)';
  const label = blocked ? `Blocked${live.blockReason ? ` · ${live.blockReason}` : ''}` : online ? 'Online' : online === false ? 'Offline' : 'Status unknown';
  return (
    <>
      <Chip colour={blocked || online === false ? 'var(--c-b91c1c)' : online ? 'var(--c-15803d)' : 'var(--text-muted)'}
        bg={blocked || online === false ? 'var(--cb-fef2f2)' : online ? 'var(--cb-f0fdf4)' : 'var(--cb-ffffff)'}
        border={blocked || online === false ? 'var(--cb-fecaca)' : online ? 'var(--cb-bbf7d0)' : 'var(--border)'} title={live.nasName ? `On ${live.nasName}` : 'Connection status'}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} /> {label}
      </Chip>
      {online && live.downMbps != null && (
        <Chip title="Live speed, measured on the NAS once a second" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 190 }}>
          <ArrowDown size={11} color="var(--c-1d4ed8)" /> {fmtMbps(live.downMbps)} <ArrowUp size={11} color="var(--c-15803d)" /> {fmtMbps(live.upMbps)}
        </Chip>
      )}
      {online && <LiveTrace points={history} />}
      {online && live.session?.since && <Chip title={`Connected since ${new Date(live.session.since).toLocaleString('en-IN')}`}>Up {fmtUptime(live.session.since)}</Chip>}
      {online && live.session?.ip && <Chip title={live.session.mac ? `MAC ${live.session.mac}` : 'IP address'}>IP {live.session.ip}</Chip>}
      {online && live.session && live.session.downBytes != null && (
        <Chip title="Data transferred in the current connection">Session ↓ {fmtBytes(live.session.downBytes)} ↑ {fmtBytes(live.session.upBytes)}</Chip>
      )}
      {live.usageToday && <Chip title="Data used today (from our own session log)">Today ↓ {fmtBytes(live.usageToday.rxBytes)} ↑ {fmtBytes(live.usageToday.txBytes)}</Chip>}
    </>
  );
}

const HISTORY_POINTS = 60; // one reading per second -> the last minute

// The last minute of the NAS's one-second speed readings, newest at the right edge: download in
// blue, upload in green. A steady line with tiny wiggles is an idle connection; a burst is a
// spike. Each point is one real reading (see LiveChipsLoader), so the graph itself shows the
// once-a-second cadence.
function LiveTrace({ points }) {
  const W = 172, H = 36;
  if (points.length < 2) return null;
  const peak = Math.max(1, ...points.map((p) => Math.max(p.down, p.up)));
  const x = (i) => ((HISTORY_POINTS - points.length + i) / (HISTORY_POINTS - 1)) * W;
  const y = (v) => H - 2 - (v / peak) * (H - 6);
  const line = (key) => points.map((p, i) => `${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const area = `${x(0).toFixed(1)},${H} ${line('down')} ${x(points.length - 1).toFixed(1)},${H}`;
  return (
    <span title={`Last ${points.length} seconds, one reading per second. Scale tops out at ${fmtMbps(peak)}.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 8px', borderRadius: 12, background: 'var(--cb-ffffff)', border: '1px solid var(--border)' }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Live speed, last minute">
        <polygon points={area} fill="var(--cl-dbeafe)" opacity="0.6" />
        <polyline points={line('down')} fill="none" stroke="var(--cl-1d4ed8)" strokeWidth="1.5" strokeLinejoin="round" />
        <polyline points={line('up')} fill="none" stroke="var(--cl-15803d)" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {points.length}s · peak {fmtMbps(peak)}
      </span>
    </span>
  );
}

// Polls one customer's live data every second and renders it. Kept in its own component so the
// once-a-second state change re-renders only these chips, not the whole inbox. A failed poll keeps
// the last values on screen; leaving the chat (or a hidden tab) stops the polling.
function LiveChipsLoader({ customerId }) {
  const [live, setLive] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let lastReadingAt = null;
    setLive(null);
    setHistory([]);
    const poll = () => {
      if (document.hidden || inFlight) return; // never stack requests if one is slow
      inFlight = true;
      whatsappInboxAPI.getLive(customerId)
        .then((r) => {
          const d = r.data?.data;
          if (cancelled || !d) return;
          setLive(d);
          // One point per DISTINCT NAS reading: polling a touch faster than the NAS refreshes
          // must not draw the same second twice, and a stalled NAS must show as a flat gap rather
          // than fresh-looking data.
          if (d.downMbps != null && d.rateAt && d.rateAt !== lastReadingAt) {
            lastReadingAt = d.rateAt;
            setHistory((h) => [...h, { at: d.rateAt, down: d.downMbps, up: d.upMbps || 0 }].slice(-HISTORY_POINTS));
          }
        })
        .catch(() => {})
        .finally(() => { inFlight = false; });
    };
    poll();
    const t = setInterval(poll, LIVE_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [customerId]);

  return <LiveChips live={live} history={history} />;
}

function Chip({ children, colour = 'var(--text)', bg = 'var(--cb-ffffff)', border = 'var(--border)', title, style }) {
  return (
    <span title={title} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, background: bg, border: `1px solid ${border}`, color: colour, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}>
      {children}
    </span>
  );
}

// The CRM record behind this number, always visible above the conversation (the fuller side panel
// only fits on wide screens). Everything a support person needs before replying: who they are,
// what they owe, when they expire, whether there is already a ticket open.
export function CustomerStrip({ customers, loading, onMore }) {
  if (loading && customers.length === 0) {
    return <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-muted)', background: 'var(--cb-f8fafc)', borderBottom: '1px solid var(--border)' }}>Loading customer…</div>;
  }
  if (customers.length === 0) return null;
  const shownCustomers = customers.slice(0, 2);

  return (
    <div style={{ background: 'var(--cb-f8fafc)', borderBottom: '1px solid var(--border)', padding: '6px 12px' }}>
      {shownCustomers.map((c) => {
        const due = Math.round(c.outstandingBalance || 0);
        const bb = c.customerType !== 'catv' && c.broadband?.expiryDate ? { label: 'Broadband', d: c.broadband.expiryDate, plan: c.broadband?.plan?.name } : null;
        const catv = c.customerType !== 'broadband' && c.catv?.expiryDate ? { label: 'CableTV', d: c.catv.expiryDate, plan: c.catv?.package?.name } : null;
        const tickets = c.openComplaints || [];
        return (
          <div key={c._id} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '2px 0' }}>
            <Link to={`/customers/${c._id}`} style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}>{c.name}</Link>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {c.customerId ? `${c.customerId} · ` : ''}{TYPE_LABEL[c.customerType] || c.customerType}
              {c.address?.area ? ` · ${c.address.area}` : ''}
            </span>
            {c.status && c.status !== 'active' && (
              <Chip colour="var(--c-ffffff)" bg={STATUS_COLOUR[c.status] || 'var(--cb-6b7280)'} border="transparent">{c.status}</Chip>
            )}
            {due > 0
              ? <Chip colour="var(--c-b91c1c)" bg="var(--cb-fef2f2)" border="var(--cb-fecaca)" title="Outstanding balance">{money(due)} due</Chip>
              : <Chip colour="var(--c-15803d)" bg="var(--cb-f0fdf4)" border="var(--cb-bbf7d0)" title="Outstanding balance">No dues</Chip>}
            {[bb, catv].filter(Boolean).map((e) => {
              const n = expiryNote(e.d);
              return (
                <Chip key={e.label} colour={n.bad ? 'var(--c-b91c1c)' : 'var(--text)'} bg={n.bad ? 'var(--cb-fef2f2)' : 'var(--cb-ffffff)'} border={n.bad ? 'var(--cb-fecaca)' : 'var(--border)'}
                  title={`${e.label}${e.plan ? ` — ${e.plan}` : ''}, expires ${fmtDate(e.d)}`}>
                  {e.label}{e.plan ? ` · ${e.plan}` : ''} · {n.text}
                </Chip>
              );
            })}
            {c.customerType !== 'catv' && <LiveChipsLoader customerId={c._id} />}
            {tickets.length > 0 && (
              <Link to={`/complaints/${tickets[0]._id}`} style={{ textDecoration: 'none' }} title={tickets.map(t => `${t.ticketNumber} ${t.title}`).join('\n')}>
                <Chip colour="var(--c-b45309)" bg="var(--cb-fffbeb)" border="var(--cb-fde68a)"><Ticket size={11} /> {tickets.length} open ticket{tickets.length > 1 ? 's' : ''}</Chip>
              </Link>
            )}
          </div>
        );
      })}
      {customers.length > shownCustomers.length && (
        <button onClick={onMore} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, padding: 0 }}>
          +{customers.length - shownCustomers.length} more account{customers.length - shownCustomers.length > 1 ? 's' : ''} on this number
        </button>
      )}
    </div>
  );
}

export function CustomerCard({ c }) {
  const expired = (d) => d && new Date(d) < new Date();
  const row = (label, value, warn) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, padding: '2px 0' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 500, color: warn ? 'var(--danger, #dc2626)' : 'inherit', textAlign: 'right' }}>{value}</span>
    </div>
  );
  const showBB = c.customerType !== 'catv';
  const showCatv = c.customerType !== 'broadband';
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--bg-card)' }}>
      <Link to={`/customers/${c._id}`} style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', textDecoration: 'none' }}>{c.name}</Link>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
        {c.customerId ? `${c.customerId} · ` : ''}{c.customerType} · {c.status}
      </div>
      {c.address && <div style={{ fontSize: 12, marginBottom: 6 }}>{[c.address.line1, c.address.area].filter(Boolean).join(', ')}</div>}
      {row('Outstanding', money(c.outstandingBalance), c.outstandingBalance > 0)}
      {showBB && c.broadband?.expiryDate !== undefined && (<>
        {row('Broadband plan', c.broadband?.plan?.name || '—')}
        {row('BB expiry', fmtDate(c.broadband?.expiryDate), expired(c.broadband?.expiryDate))}
        {c.broadband?.username && row('PPPoE user', c.broadband.username)}
      </>)}
      {showCatv && c.catv?.expiryDate !== undefined && (<>
        {row('CATV package', c.catv?.package?.name || '—')}
        {row('CATV expiry', fmtDate(c.catv?.expiryDate), expired(c.catv?.expiryDate))}
      </>)}

      <div style={{ fontWeight: 600, fontSize: 12, margin: '10px 0 4px' }}>Open complaints</div>
      {c.openComplaints.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>None</div>}
      {c.openComplaints.map(t => (
        <Link key={t._id} to={`/complaints/${t._id}`} style={{ display: 'block', fontSize: 12, color: 'inherit', textDecoration: 'none', padding: '2px 0' }}>
          <b>{t.ticketNumber}</b> · {t.title} <span style={{ color: 'var(--text-muted)' }}>({t.status})</span>
        </Link>
      ))}

      <div style={{ fontWeight: 600, fontSize: 12, margin: '10px 0 4px' }}>Recent payments</div>
      {c.recentPayments.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>None</div>}
      {c.recentPayments.map(p => (
        <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
          <span>{fmtDate(p.paymentDate)} · {String(p.paymentFor || '').replace(/_/g, ' ')}</span>
          <b>{money(p.totalAmount)}</b>
        </div>
      ))}
    </div>
  );
}

// Assignment, open/resolved and staff-only notes. None of this is visible to the customer.
export function WorkflowBox({ meta, staff, canReply, onSaveMeta, onAddNote }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const resolved = meta?.status === 'resolved';

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, background: 'var(--bg-card)' }}>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Handling</div>
      <select value={meta?.assignedTo || ''} disabled={!canReply} onChange={(e) => onSaveMeta({ assignedTo: e.target.value || null })}
        style={{ width: '100%', padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, marginBottom: 8 }}>
        <option value="">Unassigned</option>
        {staff.map(u => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}
      </select>
      <button className={resolved ? 'btn btn-secondary' : 'btn btn-success'} disabled={!canReply}
        onClick={() => onSaveMeta({ status: resolved ? 'open' : 'resolved' })} style={{ width: '100%', fontSize: 12 }}>
        <CheckCircle2 size={14} /> {resolved ? 'Reopen conversation' : 'Mark resolved'}
      </button>

      <div style={{ fontWeight: 600, fontSize: 12, margin: '12px 0 6px' }}>
        <StickyNote size={12} style={{ verticalAlign: -2 }} /> Internal notes
      </div>
      {(meta?.notes || []).length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No notes yet.</div>}
      {(meta?.notes || []).slice().reverse().map((n, i) => (
        <div key={n._id || i} style={{ background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
          <div style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{n.text}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{n.byName || 'Staff'} · {fmtDate(n.at)} {fmtTime(n.at)}</div>
        </div>
      ))}
      {canReply && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for staff only"
            onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) { e.preventDefault(); setBusy(true); onAddNote(note.trim()).finally(() => { setNote(''); setBusy(false); }); } }}
            style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }} />
          <button className="btn btn-secondary" disabled={busy || !note.trim()} style={{ fontSize: 12 }}
            onClick={() => { setBusy(true); onAddNote(note.trim()).finally(() => { setNote(''); setBusy(false); }); }}>Add</button>
        </div>
      )}
    </div>
  );
}

// `withLive` (the phone sheet only) adds each broadband customer's live connection. It polls once a
// second, so it is only mounted while the sheet is open.
function InfoPanel({ chat, customers, loading, meta, staff, canReply, onSaveMeta, onAddNote, withLive }) {
  return (
    <div style={{ padding: 12, overflowY: 'auto', height: '100%' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{displayName(chat)}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {chat.isGroup
          ? <><Users size={12} /> Group · {chat.participantCount || 0} members</>
          : <><Phone size={12} /> {chat.phone ? <a href={`tel:+${chat.phone}`} style={{ color: 'inherit' }}>{fmtPhone(chat.phone)}</a> : 'Number hidden by WhatsApp'}</>}
      </div>
      {withLive && customers.some(c => c.customerType !== 'catv') && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <LiveChipsLoader customerId={customers.find(c => c.customerType !== 'catv')._id} />
        </div>
      )}
      <WorkflowBox meta={meta} staff={staff} canReply={canReply} onSaveMeta={onSaveMeta} onAddNote={onAddNote} />
      {chat.isGroup && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Groups aren't linked to customers. Each message shows who wrote it — by saved name, CRM customer, or WhatsApp name.
        </div>
      )}
      {!chat.isGroup && loading && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading customer…</div>}
      {!chat.isGroup && !loading && customers.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          <User size={14} style={{ verticalAlign: -2 }} /> No CRM customer has this number.
        </div>
      )}
      {customers.map(c => <CustomerCard key={c._id} c={c} />)}
    </div>
  );
}

// Below the wide layout the permanent customer strip would eat the screen. This is one tappable
// line (name, dues, anything overdue, open tickets) that opens the full customer sheet.
function CustomerSummary({ customers, loading, onOpen }) {
  if (loading && customers.length === 0) return <div className="wi-sum muted">Loading customer…</div>;
  if (customers.length === 0) return <div className="wi-sum warn">Not linked to a customer</div>;
  const c = customers[0];
  const due = Math.round(c.outstandingBalance || 0);
  const dates = [c.customerType !== 'catv' && c.broadband?.expiryDate, c.customerType !== 'broadband' && c.catv?.expiryDate].filter(Boolean);
  const exp = dates.length ? expiryNote(dates.reduce((a, b) => (new Date(a) < new Date(b) ? a : b))) : null;
  const tickets = (c.openComplaints || []).length;
  return (
    <button type="button" className="wi-sum" onClick={onOpen} aria-label="Open customer details">
      <b className="wi-sum-name">{c.name}</b>
      {c.status && c.status !== 'active' && <span className="wi-pill bad">{c.status}</span>}
      <span className={`wi-pill ${due > 0 ? 'bad' : 'ok'}`}>{due > 0 ? `${money(due)} due` : 'No dues'}</span>
      {exp?.bad && <span className="wi-pill bad">{exp.text}</span>}
      {tickets > 0 && <span className="wi-pill warn"><Ticket size={11} /> {tickets}</span>}
      {customers.length > 1 && <span className="wi-more">+{customers.length - 1}</span>}
      <ChevronRight size={16} className="wi-chev" />
    </button>
  );
}

// The phone's chat view: a full-screen layer above the app's header and tab bar, sized to the
// visible area so the composer rides above the keyboard (see useVisualViewport).
function ChatScreen({ children }) {
  const box = useVisualViewport();
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div className={`wi-screen${box.keyboard ? ' kb' : ''}`} data-no-ptr
      style={{ height: box.height, transform: box.top ? `translateY(${box.top}px)` : undefined }}>
      {children}
    </div>,
    document.body,
  );
}

function ActionRow({ icon, label, hint, disabled, onClick }) {
  return (
    <button type="button" className="wi-action" disabled={disabled} onClick={onClick}>
      <span className="wi-action-ico">{icon}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600 }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>}
      </span>
    </button>
  );
}

export default function WhatsAppInboxPage() {
  useDarkReady();
  const { hasModuleRight, isAdmin } = useAuth();
  const canReply = hasModuleRight('whatsappInbox', 'reply');
  const canRaiseComplaint = hasModuleRight('complaints', 'add');
  const canShareInvoice = hasModuleRight('payments', 'shareReceipt');
  // Pairing a device is a gateway-level action, not an inbox one.
  const canImportHistory = isAdmin || hasModuleRight('whatsappGateway', 'manage');
  const width = useWidth();
  const wide = width >= WIDE_PX;
  const narrow = width < NARROW_PX;

  const [chats, setChats] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [gatewayError, setGatewayError] = useState('');
  const [live, setLive] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [assignedFilter, setAssignedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState(''); // '' = everything | 'chat' | 'group'
  const [listLimit, setListLimit] = useState(60);
  const [chatsTotal, setChatsTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [staff, setStaff] = useState([]);
  const [unansweredOnly, setUnansweredOnly] = useState(false); // narrows the loaded page: their message is the last one
  const [openedUnread, setOpenedUnread] = useState(0); // unread count when the chat was opened -> "N unread messages" divider
  const [dividerId, setDividerId] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const [showInfo, setShowInfo] = useState(false); // below the wide layout: the customer sheet
  const [showSide, setShowSide] = useState(false); // wide screens: info panel docked on the right, closed until asked for
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [askingOlder, setAskingOlder] = useState(false);
  const [modal, setModal] = useState(null); // 'new' | 'template' | 'paylink' | 'complaint' | 'history' | 'actions' | 'customer'
  const scrollRef = useRef(null);
  const stickBottom = useRef(true);
  const fileInput = useRef(null);
  const photoInput = useRef(null);
  const textRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;
  const snapRef = useRef(null);
  const historyPushed = useRef(false);
  const touch = useMemo(isTouchDevice, []);

  const [selectedSnap, setSelectedSnap] = useState(null);
  const selected = useMemo(() => (selectedId ? chats.find(c => c.chatId === selectedId) || selectedSnap : null), [chats, selectedId, selectedSnap]);
  // Quick actions act on the CRM customer behind the number; with no match there is nothing to act on.
  const actionCustomer = customers[0] || null;

  // Phones: a chat is its own full-screen view, so it gets a history entry. The Android back button
  // (or browser back) then returns to the list instead of leaving the inbox.
  const openChat = (chat) => {
    snapRef.current = chat;
    setSelectedSnap(chat);
    setSelectedId(chat.chatId);
    setShowInfo(false);
    if (narrow && !historyPushed.current) {
      window.history.pushState({ ...window.history.state, wiChat: 1 }, '');
      historyPushed.current = true;
    }
  };
  const closeChat = () => {
    if (historyPushed.current) window.history.back(); // the popstate handler below does the closing
    else setSelectedId(null);
  };
  useEffect(() => {
    const onPop = () => {
      if (!historyPushed.current) return;
      historyPushed.current = false;
      setSelectedId(null);
      setShowInfo(false);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setListLimit(60); }, [debouncedSearch, unreadOnly, assignedFilter, statusFilter, typeFilter]);

  useEffect(() => { whatsappInboxAPI.getStaff().then(r => setStaff(r.data.data)).catch(() => setStaff([])); }, []);

  const loadChats = useCallback(async () => {
    try {
      const res = await whatsappInboxAPI.getChats({
        limit: listLimit,
        type: typeFilter || undefined,
        search: debouncedSearch || undefined,
        unread: unreadOnly ? 'true' : undefined,
        assignedTo: assignedFilter || undefined,
        // Searching looks through resolved chats too, like any messaging app.
        chatStatus: (debouncedSearch && statusFilter === 'open') ? undefined : statusFilter || undefined,
      });
      setChats(res.data.data);
      setChatsTotal(res.data.total || 0);
      setGatewayError('');
    } catch (err) {
      setGatewayError(err.response?.data?.message || 'Could not load chats');
    } finally { setListLoaded(true); }
  }, [debouncedSearch, unreadOnly, assignedFilter, statusFilter, typeFilter, listLimit]);

  useEffect(() => {
    loadChats();
    const t = setInterval(() => { if (!document.hidden) loadChats(); }, LIST_POLL_MS);
    return () => clearInterval(t);
  }, [loadChats]);

  const mergeMessages = useCallback((incoming) => {
    setMessages((prev) => {
      const map = new Map(prev.map(m => [m.waId, m]));
      incoming.forEach(m => map.set(m.waId, m));
      return [...map.values()].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
  }, []);

  const loadMessages = useCallback(async (chatId, { first = false } = {}) => {
    try {
      const res = await whatsappInboxAPI.getMessages(chatId, { limit: 60 });
      if (selectedRef.current !== chatId) return;
      mergeMessages(res.data.data);
      if (first) setHasMore(res.data.hasMore);
    } catch { /* transient — the backstop poll retries */ }
  }, [mergeMessages]);

  // Live stream. Refreshes are coalesced: a burst of events (a history chunk, several quick
  // replies) must not fire one request per message.
  const refreshTimer = useRef(null);
  const loadChatsRef = useRef(loadChats);
  loadChatsRef.current = loadChats;
  const loadMessagesRef = useRef(loadMessages);
  loadMessagesRef.current = loadMessages;

  useEffect(() => {
    const scheduleRefresh = (chatId) => {
      if (refreshTimer.current) return;
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        loadChatsRef.current();
        if (chatId && selectedRef.current === chatId) loadMessagesRef.current(chatId);
      }, 600);
    };
    const stop = openInboxStream({
      onEvent: (ev) => {
        setLive(true);
        if (ev.type === 'status') {
          setMessages(prev => prev.map(m => (m.waId === ev.waId ? { ...m, status: ev.status } : m)));
          return;
        }
        if (ev.type === 'message' && ev.direction === 'in' && ev.chatId === selectedRef.current) {
          whatsappInboxAPI.markRead(ev.chatId).catch(() => {});
        }
        scheduleRefresh(ev.chatId);
      },
      onOpen: () => setLive(true),
      onError: () => setLive(false),
    });
    return () => { stop(); if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, []);

  // Open a chat: reset conversation state, load history, customer panel and workflow meta.
  useEffect(() => {
    if (!selectedId) return undefined;
    setMessages([]); setPending([]); setHasMore(false); setText(''); setFile(null); setMeta(null);
    setOpenedUnread(snapRef.current?.chatId === selectedId ? snapRef.current.unreadCount || 0 : 0);
    setDividerId(null); setShowJump(false);
    stickBottom.current = true;
    loadMessages(selectedId, { first: true });
    setCustomers([]);
    if (!selectedRef.current || !String(selectedRef.current).endsWith('@g.us')) {
      setCustomersLoading(true);
      whatsappInboxAPI.getCustomers(selectedId)
        .then(r => setCustomers(r.data.data))
        .catch(() => setCustomers([]))
        .finally(() => setCustomersLoading(false));
    }
    whatsappInboxAPI.getMeta(selectedId).then(r => setMeta(r.data.data)).catch(() => setMeta(null));
    whatsappInboxAPI.markRead(selectedId).catch(() => {});
    setChats(cs => cs.map(c => (c.chatId === selectedId ? { ...c, unreadCount: 0 } : c)));
    const t = setInterval(() => { if (!document.hidden) loadMessages(selectedId); }, CHAT_POLL_MS);
    return () => clearInterval(t);
  }, [selectedId, loadMessages]);

  useEffect(() => {
    if (selected && selected.unreadCount > 0 && !document.hidden) {
      whatsappInboxAPI.markRead(selected.chatId).catch(() => {});
      setChats(cs => cs.map(c => (c.chatId === selected.chatId ? { ...c, unreadCount: 0 } : c)));
    }
  }, [selected]);

  useEffect(() => {
    if (stickBottom.current && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  // The keyboard opening/closing resizes the message area: keep the latest message in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (!selectedId || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => { if (stickBottom.current) el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedId]);

  // Fix the "N unread messages" divider on the first unread message once, so later incoming
  // messages (already read, since the chat is open) don't drag it upwards.
  useEffect(() => {
    if (dividerId || !openedUnread || !messages.length) return;
    let n = 0;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].direction === 'in' && ++n === openedUnread) { setDividerId(messages[i].waId); return; }
    }
  }, [messages, openedUnread, dividerId]);

  // Grow the composer with its text, up to a few lines.
  useEffect(() => {
    const ta = textRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [text, selectedId]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickBottom.current = gap < 80;
    setShowJump(gap > 320);
  };
  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (el) { stickBottom.current = true; el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }
  };

  const loadOlder = async () => {
    if (!messages.length) return 0;
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    try {
      const res = await whatsappInboxAPI.getMessages(selectedId, { limit: 60, before: messages[0].timestamp });
      stickBottom.current = false;
      mergeMessages(res.data.data);
      setHasMore(res.data.hasMore);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
      return res.data.data.length;
    } catch { toast.error('Could not load older messages'); return 0; }
  };

  // Nothing older is stored locally: ask the linked phone for more, then re-check shortly.
  const askPhoneForOlder = async () => {
    const chatId = selectedId;
    setAskingOlder(true);
    try {
      await whatsappInboxAPI.loadOlder(chatId);
      toast('Asked your phone for older messages…');
      await new Promise((r) => setTimeout(r, 8000));
      if (selectedRef.current !== chatId) return;
      const n = await loadOlder();
      if (!n) toast('No older messages came back from the phone');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not ask the phone for older messages');
    } finally { setAskingOlder(false); }
  };

  const send = async () => {
    const body = text.trim();
    if (!selectedId || sending || (!body && !file)) return;
    setSending(true);
    const tempId = `tmp-${Date.now()}`;
    setPending(p => [...p, { waId: tempId, pending: true, direction: 'out', type: 'text', text: file ? `📎 ${file.name}${body ? `\n${body}` : ''}` : body, timestamp: new Date().toISOString() }]);
    stickBottom.current = true;
    const chatId = selectedId;
    const sendingFile = file;
    setText(''); setFile(null);
    try {
      if (sendingFile) await whatsappInboxAPI.sendMedia(chatId, sendingFile, body);
      else await whatsappInboxAPI.sendText(chatId, body);
      await loadMessages(chatId);
      loadChats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
      if (selectedRef.current === chatId) { setText(body); setFile(sendingFile); }
    } finally {
      setPending(p => p.filter(m => m.waId !== tempId));
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !touch) { e.preventDefault(); send(); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 16 * 1024 * 1024) return toast.error('File is larger than 16 MB');
    setFile(f);
  };

  // ── Quick actions ──
  const afterAction = async (message) => {
    toast.success(message);
    setModal(null);
    await loadMessages(selectedId);
    loadChats();
  };

  const sendTemplate = async (tpl) => {
    try {
      await whatsappInboxAPI.sendTemplate(selectedId, { templateId: tpl._id, customerId: actionCustomer._id });
      await afterAction(`Sent "${tpl.name}"`);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not send the template'); }
  };

  const sendPaymentLink = async (amount) => {
    try {
      const res = await whatsappInboxAPI.sendPaymentLink(selectedId, { customerId: actionCustomer._id, amount });
      await afterAction(res.data.message || 'Payment link sent');
    } catch (err) { toast.error(err.response?.data?.message || 'Could not send the payment link'); }
  };

  const sendInvoice = async () => {
    const payment = actionCustomer?.recentPayments?.[0];
    if (!payment) return toast.error('This customer has no payment to share yet');
    try {
      await paymentAPI.shareWhatsApp(payment._id);
      await afterAction(`Invoice ${payment.receiptNumber} sent`);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not send the invoice'); }
  };

  const onComplaintCreated = async (complaint) => {
    setModal(null);
    toast.success(`Ticket ${complaint.ticketNumber} created`);
    whatsappInboxAPI.getCustomers(selectedId).then(r => setCustomers(r.data.data)).catch(() => {});
    await loadMessages(selectedId);
  };

  // ── Workflow ──
  const saveMeta = async (patch) => {
    try {
      const res = await whatsappInboxAPI.saveMeta(selectedId, patch);
      setMeta(res.data.data);
      loadChats();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not update'); }
  };

  const addNote = async (noteText) => {
    try {
      const res = await whatsappInboxAPI.addNote(selectedId, noteText);
      setMeta(res.data.data);
      loadChats();
    } catch (err) { toast.error(err.response?.data?.message || 'Could not add the note'); }
  };

  const openChatFromModal = (chat, label) => {
    setModal(null);
    openChat(chat);
    loadChats();
    toast.success(`Chat opened with ${label}`);
  };

  const shown = useMemo(() => [...messages, ...pending], [messages, pending]);
  const infoProps = { customers, loading: customersLoading, meta, staff, canReply, onSaveMeta: saveMeta, onAddNote: addNote };
  const resolved = meta?.status === 'resolved';
  const needCustomer = () => toast('Link this number to a CRM customer first');

  // ── List: filter chips ──
  const resetFilters = () => { setUnreadOnly(false); setUnansweredOnly(false); setAssignedFilter(''); setStatusFilter('open'); setTypeFilter(''); };
  const unreadChats = chats.filter(c => c.unreadCount > 0).length;
  const chips = [
    ['all', 'All', !unreadOnly && !unansweredOnly && !assignedFilter && statusFilter === 'open' && !typeFilter, resetFilters, 0],
    ['unread', 'Unread', unreadOnly, () => setUnreadOnly(v => !v), unreadOnly ? 0 : unreadChats],
    ['unanswered', 'Unanswered', unansweredOnly, () => setUnansweredOnly(v => !v), 0],
    ['mine', 'Mine', assignedFilter === 'me', () => setAssignedFilter(v => (v === 'me' ? '' : 'me')), 0],
    ['unassigned', 'Unassigned', assignedFilter === 'unassigned', () => setAssignedFilter(v => (v === 'unassigned' ? '' : 'unassigned')), 0],
    ['groups', 'Groups', typeFilter === 'group', () => setTypeFilter(v => (v === 'group' ? '' : 'group')), 0],
    ['resolved', 'Resolved', statusFilter === 'resolved', () => setStatusFilter(v => (v === 'resolved' ? 'open' : 'resolved')), 0],
  ];
  const renderChip = ([key, label, on, onClick, count]) => (
    <button key={key} type="button" className={`wi-chip${on ? ' on' : ''}`} aria-pressed={on} onClick={onClick}>
      {label}{count > 0 && <span className="wi-chip-n">{count}</span>}
    </button>
  );
  const moreOn = chips.slice(3).some(c => c[2]);
  const filtered = !!(debouncedSearch || unreadOnly || unansweredOnly || assignedFilter || typeFilter || statusFilter !== 'open');
  // "Unanswered" = their message is the last one in an open 1:1 chat. The gateway can't filter on
  // that, so it narrows the page already loaded.
  const shownChats = unansweredOnly
    ? chats.filter(c => !c.isGroup && c.lastMessageDirection === 'in' && c.chatStatus !== 'resolved')
    : chats;

  const listPane = (
    <div className={`wi-list${narrow ? ' full' : ''}`}>
      <div className="wi-list-head">
        <div className="wi-search-row">
          <div className="wi-search">
            <Search size={16} />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number or message" aria-label="Search conversations" />
            {search
              ? <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={16} /></button>
              : <span className={`wi-dot${live ? ' on' : ''}`} title={live ? 'Live updates connected' : 'Reconnecting — updates may lag'} />}
          </div>
          {canImportHistory && (
            <button className="wi-icon-btn" onClick={() => setModal('history')} title="Import old chats" aria-label="Import old chats"><History size={20} /></button>
          )}
        </div>
        <div className="wi-chips" role="group" aria-label="Filter conversations">
          {chips.slice(0, 3).map(renderChip)}
          <button type="button" className={`wi-chip${moreOn ? ' on' : ''}`} onClick={() => setModal('filters')}>
            <SlidersHorizontal size={14} /> Filters{moreOn && <span className="wi-chip-n">{chips.slice(3).filter(c => c[2]).length}</span>}
          </button>
        </div>
        {!live && listLoaded && <div className="wi-offline">Reconnecting — new messages may appear late</div>}
      </div>
      <div className="wi-rows" data-no-ptr>
        {gatewayError && <div style={{ padding: 12, fontSize: 13, color: 'var(--danger, #dc2626)' }}>{gatewayError}</div>}
        {!listLoaded && <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
        {listLoaded && !gatewayError && shownChats.length === 0 && (
          <div style={{ padding: 20, fontSize: 14, color: 'var(--text-muted)', textAlign: 'center' }}>
            {filtered ? 'No chats match these filters.' : 'No conversations yet.'}
            {filtered && <div><button type="button" className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => { setSearch(''); resetFilters(); }}>Clear filters</button></div>}
          </div>
        )}
        {shownChats.map(c => <ChatRow key={c.chatId} chat={c} active={c.chatId === selectedId} onClick={() => openChat(c)} />)}
        {chatsTotal > listLimit && listLimit < 500 && (
          <div style={{ padding: 10, textAlign: 'center' }}>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setListLimit(l => Math.min(l + 60, 500))}>
              Load more ({Math.min(listLimit, chatsTotal)} of {chatsTotal})
            </button>
          </div>
        )}
      </div>
      {canReply && (
        <button className="wi-fab" onClick={() => setModal('new')} title="New chat" aria-label="New chat"><Plus size={26} /></button>
      )}
    </div>
  );

  // Wide layouts keep the always-visible quick-action row; on phones the same actions live in the
  // composer's + sheet so they don't take screen height from the conversation.
  const quickActions = !narrow && canReply && actionCustomer && (
    <div style={{ display: 'flex', gap: 6, padding: '6px 10px', background: 'var(--bg-card)', borderTop: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}><Zap size={12} /> {actionCustomer.name}:</span>
      <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setModal('template')}><FileText size={13} /> Template</button>
      <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setModal('paylink')}><Link2 size={13} /> Pay link</button>
      {canShareInvoice && (
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={sendInvoice} disabled={!actionCustomer.recentPayments?.length}><Receipt size={13} /> Invoice</button>
      )}
      {canRaiseComplaint && (
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setModal('complaint')}><Ticket size={13} /> Complaint</button>
      )}
    </div>
  );

  const openCustomer = () => (wide ? setShowSide(v => !v) : setShowInfo(true));

  const conversationPane = selected ? (
    <div className="wi-chat">
      <div className="wi-chat-head">
        {narrow && <button className="wi-icon-btn" onClick={closeChat} aria-label="Back to inbox"><ArrowLeft size={22} /></button>}
        <Avatar name={displayName(selected)} isGroup={selected.isGroup} size={38} />
        <button type="button" className="wi-chat-who" onClick={openCustomer}>
          <span className="wi-chat-name">{displayName(selected)}</span>
          <span className="wi-chat-sub">
            {selected.isGroup ? `Group · ${selected.participantCount || 0} members` : (selected.phone ? fmtPhone(selected.phone) : 'Number hidden by WhatsApp')}
            {selected.savedName && selected.customers?.[0]?.name && selected.customers[0].name !== selected.savedName && ` · CRM: ${selected.customers[0].name}`}
            {meta?.assignedToName && ` · ${meta.assignedToName}`}
            {resolved && ' · Resolved'}
          </span>
        </button>
        {!selected.isGroup && selected.phone && (
          <a className="wi-icon-btn" href={`tel:+${selected.phone}`} aria-label="Call customer"><Phone size={20} /></a>
        )}
        <button className={`wi-icon-btn${!narrow ? ' wide' : ''}`} aria-pressed={wide ? showSide : showInfo} onClick={openCustomer} aria-label="Customer details">
          <Info size={20} />{!narrow && <span>Customer</span>}
        </button>
      </div>

      {!selected.isGroup && (wide
        ? <CustomerStrip customers={customers} loading={customersLoading} onMore={() => setShowSide(true)} />
        : <CustomerSummary customers={customers} loading={customersLoading} onOpen={() => setShowInfo(true)} />)}

      <div className="wi-msgs-wrap">
        <div ref={scrollRef} onScroll={onScroll} className="wi-msgs" data-no-ptr>
          {shown.length > 0 && (
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              {hasMore ? (
                <button className="btn btn-secondary" onClick={loadOlder} style={{ fontSize: 12 }}><ChevronDown size={12} style={{ transform: 'rotate(180deg)' }} /> Load older messages</button>
              ) : (
                <button className="btn btn-secondary" onClick={askPhoneForOlder} disabled={askingOlder} style={{ fontSize: 12 }}>
                  {askingOlder ? 'Asking your phone…' : 'Ask phone for older messages'}
                </button>
              )}
            </div>
          )}
          {shown.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 20 }}>
              No messages yet — send the first one below.
            </div>
          )}
          {shown.map((m, i) => (
            <React.Fragment key={m.waId}>
              {(i === 0 || !isSameDay(shown[i - 1].timestamp, m.timestamp)) && (
                <div style={{ textAlign: 'center', margin: '10px 0' }}>
                  <span style={{ background: 'var(--cb-e1f3fb)', fontSize: 12, padding: '3px 10px', borderRadius: 8 }}>{fmtDay(m.timestamp)}</span>
                </div>
              )}
              {m.waId === dividerId && <div className="wi-unread-div">{openedUnread} unread message{openedUnread > 1 ? 's' : ''}</div>}
              <Bubble msg={m} showSender={!!selected.isGroup && m.direction === 'in'} />
            </React.Fragment>
          ))}
        </div>
        {showJump && <button type="button" className="wi-jump" onClick={jumpToLatest} aria-label="Jump to latest message"><ArrowDown size={18} /></button>}
      </div>

      {quickActions}

      {canReply ? (
        <div className="wi-composer">
          {file && (
            <div className="wi-file">
              <Paperclip size={14} /> <span>{file.name}</span>
              <button onClick={() => setFile(null)} aria-label="Remove attachment"><X size={16} /></button>
            </div>
          )}
          <div className="wi-comp-row">
            <input ref={fileInput} type="file" hidden onChange={onPickFile} />
            <input ref={photoInput} type="file" accept="image/*,video/*" hidden onChange={onPickFile} />
            {narrow ? (
              <>
                <button className="wi-icon-btn" onClick={() => setModal('actions')} disabled={sending} aria-label="Attach a file or more actions"><Plus size={24} /></button>
                <button className="wi-icon-btn" onClick={() => (actionCustomer ? setModal('template') : needCustomer())} aria-label="Send a saved template"><Zap size={20} /></button>
              </>
            ) : (
              <button className="wi-icon-btn" onClick={() => fileInput.current?.click()} disabled={sending} aria-label="Attach file"><Paperclip size={20} /></button>
            )}
            <textarea ref={textRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} rows={1}
              placeholder={file ? 'Add a caption (optional)' : (touch ? 'Message' : 'Type a message — Enter to send, Shift+Enter for a new line')} />
            <button className="wi-send" onMouseDown={(e) => e.preventDefault()} onClick={send} disabled={sending || (!text.trim() && !file)} aria-label="Send"><Send size={20} /></button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 10, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', background: 'var(--bg-card)' }}>You have view-only access to this inbox.</div>
      )}
    </div>
  ) : (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-muted)', background: 'var(--bg)' }}>
      Select a conversation
    </div>
  );

  const closeThen = (fn) => () => { setModal(null); fn(); };

  return (
    <div>
      <div className={`wi-shell${narrow ? ' wi-narrow' : ''}`}>
        {listPane}
        {!narrow && conversationPane}
        {wide && selected && showSide && (
          <div className="wi-side">
            <InfoPanel chat={selected} {...infoProps} />
          </div>
        )}
      </div>
      {narrow && selected && <ChatScreen>{conversationPane}</ChatScreen>}

      {!wide && selected && showInfo && (
        <Modal title={displayName(selected)} onClose={() => setShowInfo(false)} width={560} variant="full">
          <InfoPanel chat={selected} {...infoProps} withLive />
        </Modal>
      )}
      {modal === 'filters' && (
        <Modal title="Filters" onClose={() => setModal(null)} variant="bottom" width={480}>
          <div className="wi-chips wrap">{chips.slice(3).map(renderChip)}</div>
          <div style={{ padding: '0 16px 16px' }}>
            <button type="button" className="btn btn-secondary" style={{ width: '100%' }} onClick={() => { resetFilters(); setModal(null); }}>Clear all filters</button>
          </div>
        </Modal>
      )}
      {modal === 'actions' && selected && (
        <Modal title="Send" onClose={() => setModal(null)} variant="bottom" width={480}>
          <ActionRow icon={<ImageIcon size={20} />} label="Photo or video" onClick={closeThen(() => photoInput.current?.click())} />
          <ActionRow icon={<FileText size={20} />} label="Document or file" hint="Up to 16 MB" onClick={closeThen(() => fileInput.current?.click())} />
          <ActionRow icon={<Zap size={20} />} label="Saved template" hint={actionCustomer ? undefined : 'Needs a linked CRM customer'} disabled={!actionCustomer} onClick={() => setModal('template')} />
          <ActionRow icon={<Link2 size={20} />} label="Payment link" hint={actionCustomer ? undefined : 'Needs a linked CRM customer'} disabled={!actionCustomer} onClick={() => setModal('paylink')} />
          {canShareInvoice && (
            <ActionRow icon={<Receipt size={20} />} label="Last invoice"
              hint={actionCustomer?.recentPayments?.length ? actionCustomer.recentPayments[0].receiptNumber : 'No payment to share yet'}
              disabled={!actionCustomer?.recentPayments?.length} onClick={closeThen(sendInvoice)} />
          )}
          {canRaiseComplaint && (
            <ActionRow icon={<Ticket size={20} />} label="Raise a complaint" hint={actionCustomer ? undefined : 'Needs a linked CRM customer'} disabled={!actionCustomer} onClick={() => setModal('complaint')} />
          )}
          <ActionRow icon={<CheckCircle2 size={20} />} label={resolved ? 'Reopen conversation' : 'Mark resolved'} onClick={closeThen(() => saveMeta({ status: resolved ? 'open' : 'resolved' }))} />
        </Modal>
      )}
      {modal === 'new' && <NewChatModal onClose={() => setModal(null)} onOpened={openChatFromModal} />}
      {modal === 'history' && <HistoryImportModal onClose={() => setModal(null)} onImported={loadChats} />}
      {modal === 'template' && actionCustomer && <TemplateModal customer={actionCustomer} onClose={() => setModal(null)} onSend={sendTemplate} />}
      {modal === 'paylink' && actionCustomer && <PaymentLinkModal customer={actionCustomer} onClose={() => setModal(null)} onSend={sendPaymentLink} />}
      {modal === 'complaint' && actionCustomer && <ComplaintModal customer={actionCustomer} onClose={() => setModal(null)} onCreated={onComplaintCreated} />}
    </div>
  );
}
